import { Box, Button, Group, Modal, PasswordInput, Stack, Text } from "@mantine/core"
import { useForm } from "@mantine/form"
import { useTranslation } from "react-i18next"
import { translateBackendError } from "../delivery/translate-error"
import { useInboundMutations } from "./hooks"

interface Props {
  opened: boolean
  onClose: () => void
  inboundOrderId: string | null
}

export function PickupStartDialog({ opened, onClose, inboundOrderId }: Props) {
  const { t } = useTranslation("inbound")
  const mutations = useInboundMutations()

  const form = useForm({
    initialValues: {
      pin: "",
    },
    validate: {
      pin: (v) => (/^\d{6}$/.test(v) ? null : t("pickupStart.pinTooShort")),
    },
  })

  const handleSubmit = async (values: typeof form.values) => {
    if (!inboundOrderId) return
    try {
      await mutations.pickupStart(inboundOrderId, { pin: values.pin })
      form.reset()
      onClose()
    } catch {
      // error surfaced via mutations.error
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t("pickupStart.title")}>
      <Box component="form" onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="sm">
          <PasswordInput
            label={t("pickupStart.pinLabel")}
            placeholder={t("pickupStart.pinPlaceholder")}
            required
            inputMode="numeric"
            maxLength={6}
            {...form.getInputProps("pin")}
          />

          {mutations.error && (
            <Text fz={14} fw={600}>
              {translateBackendError(mutations.error)}
            </Text>
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose}>
              {t("pickupStart.cancel")}
            </Button>
            <Button type="submit" loading={mutations.submitting}>
              {t("pickupStart.submit")}
            </Button>
          </Group>
        </Stack>
      </Box>
    </Modal>
  )
}
